<template>
  <div class="min-h-screen bg-[#F6F4F0] flex flex-col">
    <BlackFridayBanner />

    <div class="flex-1 container mx-auto px-4 py-8">
      <div class="max-w-xl mx-auto">
        <BackButton
          v-if="showBackButton"
          @click="previousStep"
        />

        <QuizContainer
          ref="quizContainerRef"
          :current-step="currentStep"
        >
          <template v-if="currentStep === TOTAL_STEPS">
            <FinalCTA />
          </template>

          <template v-else-if="currentStep === TOTAL_STEPS - 1">
            <AboutAline
              @next="nextStep"
            />
          </template>

          <template v-else-if="currentStep === TOTAL_STEPS - 2">
            <Testimonials
              @next="nextStep"
            />
          </template>

          <template v-else-if="currentStep === TOTAL_STEPS - 3">
            <MotivationResult
              :number="numerologyResults?.motivation || 0"
              @next="nextStep"
            />
          </template>

          <template v-else-if="currentStep === TOTAL_STEPS - 4">
            <SoulResult
              :number="numerologyResults?.soul || 0"
              @next="nextStep"
            />
          </template>

          <template v-else-if="currentStep === 10">
            <QuizInsight @continue="nextStep" />
          </template>

          <template v-else>
            <QuizQuestion
              :question="currentQuestion.question"
              :options="currentQuestion.options"
              @select="selectAnswer"
            />
          </template>
        </QuizContainer>
      </div>
    </div>

    <LegalFooter />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import BlackFridayBanner from '../BlackFridayBanner.vue'
import LegalFooter from '../LegalFooter.vue'
import BackButton from '../common/BackButton.vue'
import QuizContainer from './QuizContainer.vue'
import QuizQuestion from './QuizQuestion.vue'
import QuizInsight from './QuizInsight.vue'
import SoulResult from '../results/SoulResult.vue'
import MotivationResult from '../results/MotivationResult.vue'
import Testimonials from './steps/Testimonials/index.vue'
import AboutAline from './steps/AboutAline/index.vue'
import FinalCTA from '../results/FinalCTA.vue'
import {
  TOTAL_STEPS,
  getQuestionByStep,
  questions,
  calculateSoulNumber,
  calculateMotivationNumber,
  sendQuizData,
  getUtmParams
} from '@/utils'

const props = defineProps<{
  fullName: string
}>()

const emit = defineEmits(['previous'])

const currentStep = ref(5) // Start at step 5
const answers = ref<Array<any>>([])
const quizContainerRef = ref<{ $el: HTMLElement } | null>(null)

const showBackButton = computed(() => {
  // Show back button on first quiz step (5) to go back to PeopleCircle
  // and on all subsequent steps except FinalCTA
  return currentStep.value >= 5 && currentStep.value < TOTAL_STEPS
})

const currentQuestion = computed(() => {
  const question = getQuestionByStep(currentStep.value)
  if (!question) {
    console.error(`No question found for step ${currentStep.value}`)
    return questions[0] // Fallback to first question
  }
  return question
})

const numerologyResults = computed(() => {
  if (!props.fullName) return null

  return {
    soul: calculateSoulNumber(props.fullName),
    motivation: calculateMotivationNumber(props.fullName)
  }
})

const selectAnswer = (answer: any) => {
  answers.value.push(answer)
  nextStep()
}

const nextStep = async () => {
  if (currentStep.value < TOTAL_STEPS) {
    currentStep.value++
    scrollToQuizContainer()

    // Send progress to webhook
    await sendQuizData({
      fullName: props.fullName,
      age: 0,
      step: `quiz_${currentStep.value}`,
      utms: getUtmParams()
    })

    // Update URL with current step
    const url = new URL(window.location.href)
    url.searchParams.set('quizStep', currentStep.value.toString())
    window.history.replaceState({}, '', url.toString())
  }
}

const previousStep = () => {
  if (currentStep.value === 5) {
    // If on first quiz step, go back to PeopleCircle
    emit('previous')
  } else {
    currentStep.value--
    answers.value.pop()
    scrollToQuizContainer()

    // Update URL with current step
    const url = new URL(window.location.href)
    url.searchParams.set('quizStep', currentStep.value.toString())
    window.history.replaceState({}, '', url.toString())
  }
}

const scrollToQuizContainer = () => {
  const element = quizContainerRef.value?.$el
  if (!element) return

  const isMobile = window.innerWidth < 768
  const offset = isMobile ? 20 : 100

  setTimeout(() => {
    const rect = element.getBoundingClientRect()
    const scrollPosition = window.pageYOffset + rect.top - offset

    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth'
    })
  }, 100)
}

onMounted(() => {
  // Check URL and localStorage for quiz step
  const params = new URLSearchParams(window.location.search)
  const quizStep = params.get('quizStep') || localStorage.getItem('currentQuizStep')

  if (quizStep) {
    const step = parseInt(quizStep)
    if (!isNaN(step) && step >= 5 && step <= TOTAL_STEPS) {
      currentStep.value = step
      // Clear stored step after using it
      localStorage.removeItem('currentQuizStep')
    }
  }
})
</script>